import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MapPin, CalendarDays, DoorOpen, Clock, Car, BedDouble, Ticket, UtensilsCrossed, Accessibility, HeartPulse } from "lucide-react";
import { IMAGES, EVENT_INFO, FAQS } from "../data/content";
import { PageHero } from "../components/shared/PageHero";
import { Reveal } from "../components/shared/Reveal";
import { SectionHead } from "../components/shared/SectionHead";

const ICONS = [MapPin, CalendarDays, DoorOpen, Clock, Car, BedDouble, Ticket, UtensilsCrossed, Accessibility, HeartPulse];

const FaqItem = ({ q, a, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/5 bg-ink-soft">
      <button
        data-testid={testId}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 p-6 text-left"
      >
        <span className="font-head text-sm font-bold uppercase tracking-wide text-bone sm:text-base">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }}>
          <Plus className="h-5 w-5 shrink-0 text-kred" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-400">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function EventPage() {
  return (
    <>
      <PageHero
        eyebrow="The Event"
        title="One Stage. Every Art."
        intro="A major multi-discipline martial arts festival for New Zealand — multiple arenas, one venue, one community. Official event details will be announced as they are confirmed."
        image={IMAGES.aotearoa}
        testId="event-page-hero"
      />
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <SectionHead
          eyebrow="Event Overview"
          title="Event Information"
          description="Full event logistics — venue, dates, timings and visitor information — will be published here as they are officially confirmed."
          testId="event-info-heading"
        />
        <div className="grid gap-px overflow-hidden border border-white/5 bg-white/5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {EVENT_INFO.map((item, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal
                key={item.label}
                delay={(i % 5) * 0.06}
                className="spotlight-card bg-ink-soft p-6"
              >
                <Icon className="h-5 w-5 text-kred" />
                <h3 className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                  {item.label}
                </h3>
                <p
                  data-testid={`event-info-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-2 font-display text-2xl uppercase tracking-wide text-gold/90"
                >
                  {item.value}
                </p>
              </Reveal>
            );
          })}
        </div>
      </section>
      <section className="border-t border-white/5 bg-[#0A0B0E]">
        <div className="mx-auto max-w-4xl px-5 py-20 sm:px-8 sm:py-28">
          <SectionHead
            eyebrow="Questions"
            title="Frequently Asked Questions"
            testId="faq-heading"
          />
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <FaqItem q={f.q} a={f.a} testId={`faq-item-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
